import { Fragment, useEffect, useState } from "react";

import { useHistory } from "react-router-dom";
import LinkIcon from "@mui/icons-material/Link";
import { Box, Button, CardContent } from "@mui/material";
import CardHeader from "@mui/material/CardHeader";
import Grid from "@mui/material/Grid2";

import { useApiContext } from "../../../../api/context";
import { alertTexts } from "../../../../assets/texts";
import { helpURLs } from "../../../../assets/urls";
import { LearnMoreAlert } from "../../../../Components/Alerts";
import { FilterTable } from "../../../../Components/FilterTable/FilterTable";
import Hide from "../../../../Components/HideChildren/Hide";
import useFiltersFromUrl from "../../../../Components/hooks/useFiltersFromUrl";
import useRouteMatchURL from "../../../../Components/hooks/useRouteMatchURL";
import { useCustomerContext } from "../../../../Context/CustomerContext";
import { deleteAccount } from "../db";
import { useAWSAccounts } from "../hooks";
import { type AWSAccount } from "../types";
import { AWSAccountsRow } from "./AWSAccountsRow";
import { filterColumns, headers } from "./columns";
import { DeleteRoleDialog } from "./DeleteRoleDialog";
import { TableSkeleton } from "./TableSkeleton";
import type { DefaultFilterColumn, LogicalOperator } from "../../../../types/FilterTable";

/**
 * Screen to show filter table with all the linked accounts
 */
export const AWSAccounts = () => {
  const { customer } = useCustomerContext();
  const api = useApiContext();
  const [accounts, numNotConfiguredAccounts] = useAWSAccounts();
  const [accountToDelete, setAccountToDelete] = useState<AWSAccount | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const routeMatchURL = useRouteMatchURL();
  const history = useHistory();

  const filtersFromQueryString = useFiltersFromUrl<AWSAccount>();
  const persistenceKey = filtersFromQueryString ? "" : "aws_accounts_manage";

  let defaultFilters: ("AND" | "OR" | DefaultFilterColumn<AWSAccount>)[] = [];
  if (filtersFromQueryString) {
    defaultFilters = filtersFromQueryString.flatMap((filter, index, array) =>
      index < array.length - 1 ? [filter, "OR" as LogicalOperator] : [filter]
    );
  }

  useEffect(() => {
    api.get(`/v1/customers/${customer.id}/cloudconnect/aws/health`).then(() => {
      setLoading(false);
    });
  }, [api, customer.id]);

  // use a row component with pre-defined delete action
  const AWSAccountsRowWrapper = (props: { row: AWSAccount }) => (
    <AWSAccountsRow row={props.row} onDeleteClicked={setAccountToDelete} />
  );

  const onLinkAccountClicked = () => {
    history.push(`${routeMatchURL}/link`);
  };

  const handleDeleteAccount = () => {
    if (accountToDelete) {
      deleteAccount(customer.id, accountToDelete.id);
    }
  };

  return (
    <>
      <>
        <CardHeader title="Link Amazon Web Services" titleTypographyProps={{ variant: "h1" }} />
        <CardContent>
          <Box
            hidden={loading}
            sx={{
              mt: -3,
              pb: 1,
            }}
          >
            <LearnMoreAlert text={alertTexts.CLOUD_CONNECT_AWS} url={helpURLs.ADD_AWS_IAM_ROLE} />
          </Box>
          {loading && <TableSkeleton />}
          <Box hidden={loading}>
            <FilterTable<AWSAccount>
              filterBarPlaceholder="Filter accounts"
              rowComponent={AWSAccountsRowWrapper}
              showRowsSelection={false}
              headerColumns={headers}
              filterColumns={filterColumns}
              tableItems={accounts}
              defaultSortingColumnValue="status"
              persistenceKey={persistenceKey}
              defaultFilters={defaultFilters}
              tagsLimit={21}
            >
              <Hide smDown>
                <Grid>
                  <Button
                    disabled={numNotConfiguredAccounts === 0}
                    variant="contained"
                    color="primary"
                    data-cy="link-account-button"
                    startIcon={<LinkIcon />}
                    onClick={onLinkAccountClicked}
                  >
                    Link account
                  </Button>
                </Grid>
              </Hide>
            </FilterTable>
          </Box>
        </CardContent>
      </>
      <DeleteRoleDialog
        accountId={accountToDelete?.accountId}
        handleDelete={handleDeleteAccount}
        onClose={() => {
          setAccountToDelete(null);
        }}
      />
    </>
  );
};
